import styled from '@emotion/styled'
import * as React from 'react'

import { useBgColorCtx } from '~theme/BgColorProvider'
import { TContrastLevel } from '~theme/typings'
import { Box, FlexCentered } from '~ui/components/layout/Box'

type TProps = NoChildren & {
  /**
   * contrast between the divider & its background
   * used to deduce color of the divider
   * from preset divider theme colors
   */
  contrast?: TContrastLevel
  /**
   * line height
   */
  thickness?: PropsOf<typeof Box>['height']
  /**
   * margin of the line, `none` by default
   */
  margin?: PropsOf<typeof Box>['margin']
}

type TStyledProps = {
  backgroundColor?: string
  afterHeight?: PropsOf<typeof Box>['height']
}

const propForwardOptions = {
  shouldForwardProp: (propName: string) =>
    propName !== `backgroundColor` && propName !== `afterHeight`,
}

const StyledHr = styled(FlexCentered, propForwardOptions)<TStyledProps>`
  &::after {
    content: '';
    background-color: ${({ backgroundColor }) => backgroundColor};
    width: 100%;
    height: ${({ afterHeight }) => afterHeight};
  }
`

/**
 * A simple full-width hr (horizontal rule) with selectable `thickness`.
 * If `color` is not defined, it is picked automatically from divider shades
 * based on the background color of the parent container.
 */
export const Hr: React.FC<TProps> = ({
  margin = `none`,
  thickness = `2px`,
  contrast = `normal`,
}) => {
  const { dividerColors } = useBgColorCtx()

  const dividerColorValue = dividerColors[contrast]

  return (
    <StyledHr
      as={`hr`}
      afterHeight={thickness}
      backgroundColor={dividerColorValue}
      border={`none`}
      margin={margin}
      opacity={1}
    />
  )
}

/**
 * A simple full-height vr (vertical rule) with selectable `thickness`.
 * If `color` is not defined, it is picked automatically from divider shades
 * based on the background color of the parent container.
 */
export const Vr: React.FC<TProps> = ({
  margin = `none`,
  thickness = `2px`,
  contrast = `normal`,
}) => {
  const { dividerColors } = useBgColorCtx()

  const dividerColorValue = dividerColors[contrast]

  return (
    <StyledHr
      as={`hr`}
      height={`100%`}
      afterHeight={`100%`}
      width={thickness}
      backgroundColor={dividerColorValue}
      border={`none`}
      margin={margin}
      opacity={1}
    />
  )
}
